import { useField } from 'formik';
import { useMemo, useState } from 'react';
import { ErrorMessage } from 'styles/Styles.styled';
import { Editor } from 'react-draft-wysiwyg';
import { ContentState, EditorState, convertFromHTML, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { ALLOWED_SYMBOLS_ERROR } from 'util/allowedSymbolsValidationUtils';

const HTMLEditor = ({ editorStyle = {}, toolbar = undefined, toolbarHidden = true, readOnly = false, ...props }: any) => {
  const [field, meta, helpers] = useField(props);
  const { setValue, setTouched } = helpers;

  const sanitizeHtml = (html: string) => html.replace(/<ins>/g, '<u>').replace(/<\/ins>/g, '</u>');

  const createEditorState = (value: string) => {
    const cleanHtml = sanitizeHtml(value || '');
    const blocksFromHTML = convertFromHTML(cleanHtml);
    const contentState = ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);
    return EditorState.createWithContent(contentState);
  };

  const [editorState, setEditorState] = useState<EditorState>(() => createEditorState(field.value));

  const rawError = meta.touched ? meta.error : undefined;

  const shouldShowBottomError =
    rawError && !props.supressErrors && (props.shouldShowBottomError ? props.shouldShowBottomError(rawError) : true);

  const shouldHighlightBorder =
    rawError && (props.shouldHighlightBorder ? props.shouldHighlightBorder(rawError) : rawError === ALLOWED_SYMBOLS_ERROR);

  const renderedError = useMemo(() => {
    if (!rawError) return null;
    return props.renderErrorMessage ? props.renderErrorMessage(rawError) : rawError;
  }, [rawError, props]);

  const onChange = (newState: EditorState) => {
    setEditorState(newState);
    const newValue = draftToHtml(convertToRaw(newState.getCurrentContent()));
    setValue(newValue);
  };

  const mergedEditorStyle = {
    ...(editorStyle ?? {}),
    ...(readOnly ? { backgroundColor: '#EEF1F4' } : null),
    ...(shouldHighlightBorder
      ? {
          border: `1px solid ${props.theme?.colors?.systemStatusError ?? 'red'}`,
        }
      : null),
  };

  return (
    <>
      <div onBlur={() => setTouched(true)}>
        <Editor
          editorStyle={mergedEditorStyle}
          editorState={editorState}
          toolbarHidden={toolbarHidden}
          toolbar={toolbar}
          editorClassName="html-editor"
          name={field.name}
          onEditorStateChange={onChange}
          readOnly={readOnly}
          {...props}
        />
      </div>

      {shouldShowBottomError ? (
        <ErrorMessage style={{ ...props.errorMessageStyle, marginTop: 5 }}>{renderedError}</ErrorMessage>
      ) : null}
    </>
  );
};

export default HTMLEditor;
